import Modal from '@mui/material/Modal';
import PropTypes from 'prop-types';
import Table from '../../common/Table/Table';
import TableBody from '../../common/Table/TableBody';
import TableCell from '../../common/Table/TableCell';
import TableHeadCell from '../../common/Table/TableHeadCell';
import TableHeader from '../../common/Table/TableHeader';
import TableRow from '../../common/Table/TableRow';
import localStyles from './styles.module.scss';

const STO_COLUMNS = [
  { title: 'SKU Name', key: 'sku', width: '220px', wrap: true },
  { title: 'UOM', key: 'uom', width: '70px' },
  { title: 'Outward Store', key: 'outwardStore', width: '150px', truncate: true },
  { title: 'Outward QTY', key: 'outwardQty', width: '100px' },
  { title: 'Outward Status', key: 'outwardStatus', width: '125px' },
  { title: 'Inward Store', key: 'inwardStore', width: '150px', truncate: true },
  { title: 'Inward QTY', key: 'inwardQty', width: '100px' },
  { title: 'Inward Status', key: 'inwardStatus', width: '125px' },
];

const STATUS_GREEN = new Set(['COMPLETED', 'SUCCESS', 'COMPLETE', 'SUCCEEDED', 'DISPATCHED']);
const STATUS_RED = new Set(['FAILED', 'FAILURE', 'ERROR', 'REJECTED']);

const getStatusPillClass = (rawStatus) => {
  if (!rawStatus || rawStatus === 'NA') return localStyles.status_pill_neutral;
  const key = String(rawStatus).replaceAll(' ', '_').toUpperCase();
  if (STATUS_GREEN.has(key)) return localStyles.status_pill_green;
  if (STATUS_RED.has(key)) return localStyles.status_pill_red;
  return localStyles.status_pill_yellow;
};

const formatStatusLabel = (rawStatus) => {
  if (!rawStatus || rawStatus === 'NA') return rawStatus || '';
  return String(rawStatus)
    .replaceAll('_', ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

const StatusCell = ({ status }) => {
  if (!status || status === 'NA') return <span>NA</span>;
  return (
    <span className={`${localStyles.status_pill} ${getStatusPillClass(status)}`}>
      {formatStatusLabel(status)}
    </span>
  );
};

const getBulkScanExpectancies = (tx) =>
  tx?.childProcesses?.find((cp) => cp.processType === 'BULK_SCAN')?.expectancies || [];

const resolveQty = (tx, exp) => {
  const isCompleted = String(tx?.status || '').toUpperCase() === 'COMPLETED';
  const qty = isCompleted ? exp?.accepted : exp?.expected;
  return qty ?? '-';
};

const formatLocation = (name, id) => {
  const idStr = id != null ? String(id) : 'NA';
  return name ? `${name} (${idStr})` : idStr;
};

const flattenStoRows = (data = []) => {
  const rows = [];
  data.forEach((sto) => {
    const { warehouseOrder, processTransactions = [] } = sto;
    const outwardTx = processTransactions.find((t) => t.processType === 'OUTWARD');
    const inwardTx = processTransactions.find((t) => t.processType === 'INWARD');

    const outwardExpectancies = getBulkScanExpectancies(outwardTx);
    const inwardExpectancies = getBulkScanExpectancies(inwardTx);

    const inwardByProductId = Object.fromEntries(
      inwardExpectancies.map((e) => [e.productId, e]),
    );
    const outwardProductIds = new Set(outwardExpectancies.map((e) => e.productId));

    const date = warehouseOrder?.createdAt
      ? warehouseOrder.createdAt.split(' ')[0]
      : '-';

    // prefer outwardTx for location names; fall back to inwardTx, then warehouseOrder IDs
    const primaryTx = outwardTx ?? inwardTx;
    const sourceStore = formatLocation(
      primaryTx?.sourceLocationName,
      primaryTx?.sourceLocationId ?? warehouseOrder?.sourceLocationId,
    );
    const destStore = formatLocation(
      primaryTx?.destinationLocationName,
      primaryTx?.destinationLocationId ?? warehouseOrder?.destinationLocationId,
    );

    // OUTWARD rows first; inward columns populated from matching INWARD expectancy
    outwardExpectancies.forEach((outExp) => {
      const inExp = inwardByProductId[outExp.productId];
      rows.push({
        id: `${sto.warehouseOrderId}-out-${outExp.id ?? outExp.productId}`,
        date,
        sku: formatLocation(outExp.productName, outExp.productId),
        uom: outExp.uom || '-',
        outwardStore: sourceStore,
        outwardQty: resolveQty(outwardTx, outExp),
        outwardStatus: outwardTx?.status || '-',
        inwardStore: destStore,
        inwardQty: inExp ? resolveQty(inwardTx, inExp) : 'NA',
        inwardStatus:
          inwardTx?.status ||
          (String(outwardTx?.status || '').toUpperCase() === 'REJECTED' ? 'REJECTED' : 'NA'),
      });
    });

    // INWARD-only rows for products that had no matching OUTWARD expectancy
    inwardExpectancies
      .filter((inExp) => !outwardProductIds.has(inExp.productId))
      .forEach((inExp) => {
        rows.push({
          id: `${sto.warehouseOrderId}-in-${inExp.id ?? inExp.productId}`,
          date,
          sku: formatLocation(inExp.productName, inExp.productId),
          uom: inExp.uom || '-',
          outwardStore: 'NA',
          outwardQty: 'NA',
          outwardStatus: 'NA',
          inwardStore: destStore,
          inwardQty: resolveQty(inwardTx, inExp),
          inwardStatus: inwardTx?.status || 'NA',
        });
      });
  });
  return rows;
};

function STODetailsModal({ open, onClose, uploadId, uploadedBy, data, loading, error }) {
  const rows = flattenStoRows(data);
  const headerDate = data[0]?.warehouseOrder?.createdAt?.split(' ')[0] || '';

  const renderBody = () => {
    if (loading) {
      return <div className={localStyles.sto_modal_state}>Loading STO details...</div>;
    }
    if (error) {
      return <div className={localStyles.table_state_error}>{error}</div>;
    }
    if (rows.length === 0) {
      return <div className={localStyles.sto_modal_state}>No STO details found.</div>;
    }
    return (
      <div className={localStyles.sto_table_scroll}>
        <Table className={localStyles.sto_table}>
          <TableHeader>
            {STO_COLUMNS.map((col) => (
              <TableHeadCell
                key={col.key}
                style={{ width: col.width, minWidth: col.width }}
                className={localStyles.sticky_head_cell}>
                {col.title}
              </TableHeadCell>
            ))}
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                {STO_COLUMNS.map((col) => {
                  const value = row[col.key] ?? '-';
                  return (
                    <TableCell key={`${row.id}-${col.key}`} style={{ width: col.width, maxWidth: col.width }}>
                      {col.key === 'outwardStatus' || col.key === 'inwardStatus' ? (
                        <StatusCell status={value} />
                      ) : col.truncate ? (
                        <span className={localStyles.sto_cell_truncate} title={value}>{value}</span>
                      ) : col.wrap ? (
                        <span className={localStyles.sto_cell_wrap}>{value}</span>
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={localStyles.sto_modal_box}>
        <div className={localStyles.sto_modal_header}>
          <div>
            <h3 className={localStyles.sto_modal_title}>
              {headerDate ? <span className={localStyles.sto_modal_date}>{headerDate} | </span> : null}
              STO Details{uploadId ? ` — ${uploadId}` : ''}
            </h3>
            {uploadedBy ? (
              <p className={localStyles.sto_modal_subtitle}>Uploaded by: {uploadedBy}</p>
            ) : null}
          </div>
          <button
            type="button"
            className={localStyles.sto_modal_close}
            onClick={onClose}
            aria-label="Close">
            ✕
          </button>
        </div>
        {renderBody()}
      </div>
    </Modal>
  );
}

STODetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  uploadId: PropTypes.string,
  uploadedBy: PropTypes.string,
  data: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

STODetailsModal.defaultProps = {
  uploadId: '',
  uploadedBy: '',
  data: [],
  loading: false,
  error: '',
};

StatusCell.propTypes = {
  status: PropTypes.string,
};

StatusCell.defaultProps = {
  status: '',
};

export default STODetailsModal;
